import { Controller } from "@hotwired/stimulus"
import store from "@/store"

const SCROLL_KEY_PREFIX = "listings-scroll:"
const HIGHLIGHT_KEY_PREFIX = "listings-highlight:"
const LISTINGS_KEY_PREFIX = "listings-cache:"
const PER_KEY_PREFIX = "listings-per:"
const COUNT_KEY_PREFIX = "listings-count:"
const HIGHLIGHT_CLASS = "animate-halo"

// Unlike sessionStorage, dropped on full reload — so a refresh always refetches.
const memoryStorage = {
  store: new Map(),
  getItem(key) {
    return this.store.has(key) ? this.store.get(key) : null
  },
  setItem(key, value) {
    this.store.set(key, value)
  },
  removeItem(key) {
    this.store.delete(key)
  },
}

const storageKey = (prefix) => `${prefix}${window.location.pathname}${window.location.search}`

export default class extends Controller {
  connect() {
    this.unsubscribeStore = null

    this.restoreFromHistory()
  }

  disconnect() {
    this.unsubscribeStore?.()
    this.unsubscribeStore = null
  }

  saveScrollPosition() {
    memoryStorage.setItem(storageKey(SCROLL_KEY_PREFIX), String(window.scrollY))
    const filterState = store.state.filter
    if (filterState?.listings?.length > 0) {
      memoryStorage.setItem(storageKey(LISTINGS_KEY_PREFIX), JSON.stringify(filterState.listings))
    }
    // Also persist pagination metadata so it can be restored alongside the cached listings,
    // otherwise the per-page selector and page count are missing until the next real fetch.
    if (filterState?.filters?.per != null) {
      memoryStorage.setItem(storageKey(PER_KEY_PREFIX), String(filterState.filters.per))
    }
    if (filterState?.listings_count != null) {
      memoryStorage.setItem(storageKey(COUNT_KEY_PREFIX), String(filterState.listings_count))
    }
    // ListingPagination sets a global window.onpopstate on every paginate that
    // re-dispatches changePage. Turbo back-navigation also triggers popstate, so
    // the stale handler would fire a spurious second fetchListings on return.
    window.onpopstate = null
  }

  captureClickedListing(event) {
    const link = event.target.closest(".ListingGrid a[href]")
    if (!link) return
    memoryStorage.setItem(storageKey(HIGHLIGHT_KEY_PREFIX), link.getAttribute("href"))
  }

  // On back-navigation: immediately restore cached listings so the grid stays populated
  // during the API round-trip (fetchListings clears the grid before it gets a response).
  // Once the real data arrives (or the cache itself is committed), jump to the saved
  // scroll position and highlight the card the user came from.
  restoreFromHistory() {
    this.unsubscribeStore?.()
    this.unsubscribeStore = null

    const savedScroll = memoryStorage.getItem(storageKey(SCROLL_KEY_PREFIX))
    const clickedHref = memoryStorage.getItem(storageKey(HIGHLIGHT_KEY_PREFIX))
    const rawCache = memoryStorage.getItem(storageKey(LISTINGS_KEY_PREFIX))
    if (savedScroll == null && clickedHref == null && rawCache == null) return

    memoryStorage.removeItem(storageKey(SCROLL_KEY_PREFIX))
    memoryStorage.removeItem(storageKey(HIGHLIGHT_KEY_PREFIX))
    // LISTINGS_KEY (and its companion pagination keys) are intentionally kept in
    // memoryStorage until the subscriber fires. If the Stimulus controller disconnects
    // and reconnects (e.g. due to a Vue re-render triggered by initListings commits),
    // restoreFromHistory will be called again and can still read rawCache to re-register
    // the subscriber with the correct cached data.
    const targetY = savedScroll == null ? null : parseInt(savedScroll, 10)

    let cachedListings = null
    try {
      cachedListings = rawCache ? JSON.parse(rawCache) : null
    } catch {
      // ignore corrupt cache
    }

    const cachedPer = parseInt(memoryStorage.getItem(storageKey(PER_KEY_PREFIX)), 10) || null
    const cachedCount = parseInt(memoryStorage.getItem(storageKey(COUNT_KEY_PREFIX)), 10) || null

    if (cachedListings?.length > 0) {
      store.commit("filter/setSkipNextFetch", true)
    }

    this.unsubscribeStore = store.subscribe((mutation) => {
      if (mutation.type !== "filter/setListings") return

      // Always clean up the cached pagination keys once the subscriber fires.
      memoryStorage.removeItem(storageKey(LISTINGS_KEY_PREFIX))
      memoryStorage.removeItem(storageKey(PER_KEY_PREFIX))
      memoryStorage.removeItem(storageKey(COUNT_KEY_PREFIX))

      // fetchListings was skipped — restore cached listings and pagination metadata.
      if (mutation.payload.length === 0 && cachedListings?.length > 0) {
        store.commit("filter/setListings", cachedListings)
        store.commit("filter/setLoading", false)
        if (cachedPer != null) store.commit("filter/setPerPage", cachedPer)
        if (cachedCount != null) store.commit("filter/setListingsCount", cachedCount)
        return
      }

      if (mutation.payload.length === 0) return

      this.unsubscribeStore?.()
      this.unsubscribeStore = null
      requestAnimationFrame(() => {
        if (targetY != null) window.scrollTo(0, targetY)
        if (clickedHref) this.highlightClickedListing(clickedHref)
      })
    })
  }

  highlightClickedListing(href) {
    const link = this.element.querySelector(`.ListingGrid a[href="${CSS.escape(href)}"]`)
    if (!link) return
    link.classList.add(HIGHLIGHT_CLASS)
  }
}
